import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { environment } from '@gb-webapp/gb-app';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { TranslateService } from '../../../services/translate/translate.service';

export const errorTranslationInterceptor: HttpInterceptorFn = (req, next) => {
  const translateService = inject(TranslateService);
  const platformId = inject(PLATFORM_ID);

  // Skip translation API requests to prevent infinite loop
  if (req.url.includes(environment.translateApiUrl)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Run error translation ONLY on client-side
      if (isPlatformBrowser(platformId) && error?.error) {
        const hasErrorsArray =
          error.error.errors &&
          Array.isArray(error.error.errors) &&
          error.error.errors.length > 0 &&
          error.error.errors[0].msg;
        const hasTopLevelMessage = !!error.error.message;

        // Translate error message (single API call, apply to both fields)
        if (hasErrorsArray || hasTopLevelMessage) {
          // Prefer inner msg, fallback to top-level message
          const originalMsg = hasErrorsArray ? error.error.errors[0].msg : error.error.message;

          return from(translateService.translate(originalMsg)).pipe(
            switchMap((translatedMsg) => {
              // Update inner error message
              if (hasErrorsArray) {
                error.error.errors[0].msg = translatedMsg;
                error.error.errors[0].originalMessage = originalMsg;
              }
              // Update top-level message
              if (hasTopLevelMessage) {
                error.error.message = translatedMsg;
                // Store original message for error detection
                error.error.originalMessage = originalMsg;
              }
              return throwError(() => error);
            }),
            catchError(() => {
              // If translation fails, return the original error
              return throwError(() => error);
            })
          );
        }
      }

      // If not in browser or no message, return the error as is
      return throwError(() => error);
    })
  );
};
